import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useBranding } from "@/hooks/useBranding";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { captureError } from "@/lib/error-message";
import { LogoUploader } from "./LogoUploader";

export function LogosTab() {
  const { getText, refetch } = useBranding();
  const [generating, setGenerating] = useState(false);
  const faviconSrc = getText("logo.favicon");
  const fav16 = getText("logo.favicon_16");
  const fav32 = getText("logo.favicon_32");
  const fav180 = getText("logo.favicon_180");
  const variantsReady = Boolean(fav16 && fav32 && fav180);

  async function generateVariants() {
    if (!faviconSrc) {
      toast.error("Upload a source favicon first.");
      return;
    }
    setGenerating(true);
    try {
      const { error } = await supabase.functions.invoke("generate-favicon-variants", {
        body: { source_url: faviconSrc },
      });
      if (error) throw error;
      await refetch();
      toast.success("Favicon variants generated.");
    } catch (e) {
      const msg = await edgeFunctionErrorMessage(e, "Variant generation failed");
      captureError(e, "branding_favicon_variants", msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Logos</CardTitle>
          <CardDescription>
            Upload horizontal logos in both light and dark variants. PNG or SVG,
            max 2 MB. We recommend a minimum height of 100 px.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 sm:grid-cols-2">
          <LogoUploader
            brandingKey="logo.light"
            label="Light background logo"
            description="Shown on white/light surfaces — home page header, emails."
            previewBg="light"
          />
          <LogoUploader
            brandingKey="logo.dark"
            label="Dark background logo"
            description="Shown on dark hero backgrounds and footer."
            previewBg="dark"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Favicon</CardTitle>
          <CardDescription>
            Upload a square source image (minimum 512×512 PNG). We'll generate
            16×16, 32×32, and 180×180 Apple touch icon variants automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <LogoUploader
            brandingKey="logo.favicon"
            label="Favicon source (512×512)"
            description="Square PNG recommended. Used as the source for all size variants."
            accept="image/png"
            previewBg="light"
          />

          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-muted/30 p-4">
            <div className="min-w-0">
              <p className="text-sm font-medium">Size variants</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {variantsReady
                  ? "All three size variants are ready."
                  : faviconSrc
                  ? "Variants have not been generated yet."
                  : "Upload a source favicon above to enable generation."}
              </p>
            </div>
            <Button
              size="sm"
              onClick={generateVariants}
              disabled={!faviconSrc || generating}
            >
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {variantsReady ? "Regenerate" : "Generate variants"}
            </Button>
          </div>

          {variantsReady && (
            <div className="flex items-center gap-6 rounded-lg border border-border/60 p-4">
              {[
                { url: fav16, size: 16, label: "16×16" },
                { url: fav32, size: 32, label: "32×32" },
                { url: fav180, size: 48, label: "180×180" },
              ].map((v) => (
                <div key={v.label} className="flex flex-col items-center gap-1">
                  <img src={v.url} alt={v.label} width={v.size} height={v.size} className="rounded" />
                  <span className="text-[10px] text-muted-foreground">{v.label}</span>
                </div>
              ))}
            </div>
          )}

          {faviconSrc && !variantsReady && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <p>
                Source uploaded. Click <strong>Generate variants</strong> to
                produce the 16/32/180 sizes used by browsers and iOS.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
